import React, { useEffect, useRef, useState } from 'react';
import ServiceCollection from 'src/services/ServiceCollection';
import ErrorUtils from 'src/utils/ErrorUtils';
import useLocalStorage from 'src/utils/useLocalStorage';
import BulkPromotionHistoryTable, {
    BulkPromotionHistoryRow,
    toBulkPromotionHistoryRows,
} from './BulkPromotionHistoryTable';
import { BULK_PROMOTION_HISTORY_DEFAULT_PREFERENCES } from './bulkPromotionHistoryConstants';

/**
 * Bulk history for the period selected above the tabs. Owns its own paging and preferences; the
 * period arrives as a prop so switching tabs cannot reset it.
 */
export default function BulkPromotionHistoryPanel(props: { services: ServiceCollection; accountingPeriod?: string }) {
    const { services, accountingPeriod } = props;
    const [preferences, setPreferences] = useLocalStorage.getStorageKeyFromLocalStorage(
        'bulk-promotion-history-table-preferences',
        BULK_PROMOTION_HISTORY_DEFAULT_PREFERENCES,
    );
    const pageSize = preferences.pageSize || BULK_PROMOTION_HISTORY_DEFAULT_PREFERENCES.pageSize;

    const [rows, setRows] = useState<BulkPromotionHistoryRow[]>([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [loadedPageCount, setLoadedPageCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const pageCacheRef = useRef<Record<number, { rows: BulkPromotionHistoryRow[], nextToken: string | null }>>({});
    const fetchIdRef = useRef(0);

    useEffect(() => {
        // The period is resolved by the page above; until it settles there is nothing to fetch and
        // nothing to wait for, so the spinner must not be left running.
        if (!accountingPeriod) {
            setIsLoading(false);
            return;
        }
        fetchIdRef.current += 1;
        const id = fetchIdRef.current;
        pageCacheRef.current = {};
        setCurrentPageIndex(1);
        setLoadedPageCount(0);
        setIsLoading(true);
        services.bulkPromotionService.listBulkPromotions(accountingPeriod, pageSize)
            .then(result => {
                if (id !== fetchIdRef.current) return;
                const page = { rows: toBulkPromotionHistoryRows(result.promotions || []), nextToken: result.nextToken || null };
                pageCacheRef.current[1] = page;
                setRows(page.rows);
                setLoadedPageCount(1);
            })
            .catch(ex => {
                if (id !== fetchIdRef.current) return;
                setRows([]);
                services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
            })
            .finally(() => {
                if (id === fetchIdRef.current) setIsLoading(false);
            });
    }, [accountingPeriod, pageSize, refreshNonce]);

    const goToPage = (pageIndex: number) => {
        const cached = pageCacheRef.current[pageIndex];
        if (cached) {
            setRows(cached.rows);
            setCurrentPageIndex(pageIndex);
        }
    };

    const fetchNextPage = async () => {
        const id = fetchIdRef.current;
        const lastPageToken = pageCacheRef.current[loadedPageCount]?.nextToken;
        if (!accountingPeriod || !lastPageToken) return;
        setIsLoading(true);
        try {
            const result = await services.bulkPromotionService.listBulkPromotions(accountingPeriod, pageSize, lastPageToken);
            if (id !== fetchIdRef.current) return;
            const newPageIndex = loadedPageCount + 1;
            pageCacheRef.current[newPageIndex] = {
                rows: toBulkPromotionHistoryRows(result.promotions || []),
                nextToken: result.nextToken || null,
            };
            setRows(pageCacheRef.current[newPageIndex].rows);
            setCurrentPageIndex(newPageIndex);
            setLoadedPageCount(newPageIndex);
        } catch (ex) {
            if (id !== fetchIdRef.current) return;
            services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
        } finally {
            if (id === fetchIdRef.current) setIsLoading(false);
        }
    };

    // The list query returns counts only, so a batch's promoted ids are fetched when its row opens.
    const loadPromotedIds = (bulkPromotionId: string) => {
        if (rows.some(row => row.bulkPromotionId === bulkPromotionId && row.promotedIds)) return;
        services.bulkPromotionService.getBulkPromotion(bulkPromotionId, accountingPeriod as string)
            .then(detail => {
                // The read answers with the batch nested under `promotion`, so an unknown or expired batch
                // yields nothing rather than an error. Reading ids off it would surface a type error where
                // the row simply has none to show.
                if (!detail) {
                    return;
                }
                const withIds = (row: BulkPromotionHistoryRow) => row.bulkPromotionId === bulkPromotionId
                    ? { ...row, promotedIds: detail.promotedIds || [] }
                    : row;
                // Written back into the cache as well as the visible rows: paging away and back
                // restores from the cache, which would otherwise serve the row without its ids.
                const cached = pageCacheRef.current[currentPageIndex];
                if (cached) {
                    pageCacheRef.current[currentPageIndex] = { ...cached, rows: cached.rows.map(withIds) };
                }
                setRows(prev => prev.map(withIds));
            })
            .catch(ex => services.messageService.showErrorBanner(ErrorUtils.getMessage(ex)));
    };

    return (
        <BulkPromotionHistoryTable
            rows={rows}
            isLoading={isLoading}
            hasNextPage={!!pageCacheRef.current[loadedPageCount]?.nextToken}
            currentPageIndex={currentPageIndex}
            pagesCount={loadedPageCount}
            onNextPage={fetchNextPage}
            onPreviousPage={() => {}}
            onPageChange={goToPage}
            onRefresh={() => setRefreshNonce(n => n + 1)}
            onExpandBatch={loadPromotedIds}
            preferences={preferences}
            onPreferencesChange={setPreferences}
        />
    );
}
